import { useTranslation } from "react-i18next";
import { DataSetFieldContainer, StyledTextField } from "pages/Datasets/Styled";
import { IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { Error, KeyValueItem, Touched } from "./types";

type ItemParams = {
  item: KeyValueItem;
  index: number;
  onChange: (fieldName: string, fieldValue: string) => void;
  onBlur: (fieldName: "key" | "value") => void;
  onRemove: () => void;
  canDelete: boolean;
  error?: Error[string];
  touched?: Touched[string];
};

export default function Item({
  item,
  index,
  onChange: emit,
  onBlur,
  onRemove,
  canDelete,
  error,
  touched,
}: ItemParams) {
  const { t } = useTranslation("datasets");

  const showKeyError = Boolean(touched?.key && error?.key);
  const showValueError = Boolean(touched?.value && error?.value);

  const errorKeyHint = () => {
    if (!showKeyError) return "";
    if (error?.key === "isBlank") return t("form.fields.key.errors.empty");
    if (error?.key === "alreadyExist")
      return t("form.fields.key.errors.duplicatedKey");
    if (error?.key === "startsWithSpace")
      return t("form.fields.key.errors.startsWithSpace");
    return "";
  };

  const errorValueHint = showValueError
    ? t("form.fields.value.errors.empty")
    : "";

  return (
    <DataSetFieldContainer
      key={`${item.id}_item`}
      data-testid={`dataset-form-row-${index}`}
    >
      <StyledTextField
        inputProps={{ "data-testid": "dataset-form-key-input" }}
        name="key"
        label={t("form.fields.key.label")}
        variant="outlined"
        fullWidth
        value={item.key}
        error={showKeyError}
        helperText={errorKeyHint()}
        onChange={event =>
          emit(event.target.name, event.target.value.trimEnd())
        }
        onBlur={() => onBlur("key")}
      />
      <StyledTextField
        inputProps={{ "data-testid": "dataset-form-value-input" }}
        name="value"
        label={t("form.fields.value.label")}
        variant="outlined"
        fullWidth
        value={item.value}
        error={showValueError}
        helperText={errorValueHint}
        onChange={event => {
          emit(event.target.name, event.target.value.trimEnd());
        }}
        onBlur={() => onBlur("value")}
      />
      <IconButton
        size="small"
        aria-label="delete"
        disabled={!canDelete}
        onClick={onRemove}
        data-testid="dataset-form-delete-row-btn"
      >
        <DeleteIcon />
      </IconButton>
    </DataSetFieldContainer>
  );
}
